import { useCallback, useEffect, useRef } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Colors, Flex, Typography } from '@banhala/ui-kit';
import type { OverviewCategory } from 'shared/src/api';
import { useLogClickExpandableCategoryList } from '../hooks/useLogClickExpandableCategoryList';
import { bridge } from 'shared/src/utils';

const MENU_ITEM_HEIGHT = 48;

interface SideMenuProps {
  categories: OverviewCategory[];
  activeIndex: number;
  scrollViewRef: React.RefObject<ScrollView | null>;
  onMenuPress: (index: number) => void;
}

export const SideMenu = ({
  categories,
  activeIndex,
  scrollViewRef,
  onMenuPress,
}: SideMenuProps) => {
  const itemRefs = useRef<(View | null)[]>([]);

  useEffect(() => {
    const targetY = activeIndex * MENU_ITEM_HEIGHT;
    scrollViewRef.current?.scrollTo({
      y: targetY - MENU_ITEM_HEIGHT * 2,
      animated: true,
    });
    bridge.vibrate('light');
  }, [activeIndex, scrollViewRef]);

  const logClickExpandableCategoryList = useLogClickExpandableCategoryList();

  const handlePress = useCallback(
    (category: OverviewCategory, index: number) => () => {
      bridge.vibrate('light');
      logClickExpandableCategoryList(category);
      onMenuPress(index);
    },
    [onMenuPress, logClickExpandableCategoryList],
  );

  return (
    <ScrollView
      ref={scrollViewRef}
      style={styles.container}
      showsVerticalScrollIndicator={false}
    >
      {categories.map((category, index) => (
        <Flex
          key={category.item.sno}
          ref={(el: View | null) => {
            itemRefs.current[index] = el;
          }}
          height={MENU_ITEM_HEIGHT}
          justifyContent="center"
          padding={{ horizontal: 12 }}
          backgroundColor={index === activeIndex ? 'white' : 'bg_secondary'}
          onPress={handlePress(category, index)}
        >
          <Typography
            type={index === activeIndex ? 'subtitle2' : 'body2'}
            color={
              index === activeIndex ? 'content_primary' : 'content_secondary'
            }
            numberOfLines={1}
          >
            {category.item.name}
          </Typography>
        </Flex>
      ))}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.bg_secondary,
  },
});
